import {
  Modal,
  ModalBody,
  ModalHeader,
  ModalTitle,
} from '@readcloud/component-library';
import {
  PDFTextHighlightAnnotation,
  TextHighlightAnnotation,
} from '@readcloud/data';
import { pdfSelectors } from '@readcloud/state';
import { Popover, PopoverContent, PopoverTrigger } from '@readcloud/ui';
import tailwindConfig from '@root/tailwind.config';
import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { TextSelectionContext } from '../../../context/TextSelection';
import { ConnectedAnnotationItem } from '../../Common/Annotations/ConnectedAnnotationItem';

interface Props {
  selectedAnnotation: TextHighlightAnnotation | PDFTextHighlightAnnotation;
  container: HTMLElement;
  boundsElement?: HTMLElement;
  zIndex?: number;
  onClose?: () => void;
  mapRangeBounds?: (rangeBounds: DOMRect, containerBounds: DOMRect) => DOMRect;
  positionFixed?: boolean;
  deselectAnnotation?: () => void;
}

export const AnnotationPopover: React.FC<Props> = (props: Props) => {
  const [target, setTarget] = React.useState<DOMRect>(new DOMRect(1, 1, 1, 1));

  //We need this popover to be positioned based on coords inside a container (the page), and popperjs only accepts an element/component as a way to position a popover.
  //so we are going to position this element where ever the popover need to show up, and then use it as a target.
  const targetRef = React.useRef<HTMLDivElement | null>(null);

  const pdfData = useSelector(pdfSelectors.getPDFData);

  const { epubContents } = useContext(TextSelectionContext);

  const mobileSizeinPx = tailwindConfig?.theme?.screens?.['sm'] || '640px';
  const mobileSize = parseInt(mobileSizeinPx, 10);

  const mobileView = React.useMemo(() => {
    if (mobileSize) {
      return window.innerWidth <= mobileSize;
    }
    return false;
  }, [mobileSizeinPx, window.innerWidth]);

  //for EPUBS
  React.useEffect(() => {
    if (props.selectedAnnotation?.data?.rects) {
      return;
    }
    if (
      props?.selectedAnnotation?.location &&
      'range' in props?.selectedAnnotation?.location
    ) {
      if (props.selectedAnnotation?.location?.range && props.container) {
        const containerBounds = props.container.getBoundingClientRect();

        const range =
          epubContents &&
          epubContents.range(props.selectedAnnotation.location.range);
        const selectionBounds = range && range?.getBoundingClientRect();

        if (selectionBounds) {
          const tempBounds = new DOMRect(
            selectionBounds.x + containerBounds.x,
            selectionBounds.y + containerBounds.y,
            selectionBounds.width,
            selectionBounds.height
          );

          setTarget(tempBounds);
        }
      }
    }
  }, [props.selectedAnnotation?.id]);

  //for PDF's
  React.useEffect(() => {
    if (!props.selectedAnnotation?.data?.rects) {
      return;
    }

    const getBoundingRect = (rects: DOMRect[]) => {
      const tempRect: DOMRect = new DOMRect(
        Number.MAX_SAFE_INTEGER,
        Number.MAX_SAFE_INTEGER,
        0,
        0
      );

      rects.forEach((rect) => {
        if (rect.x * pdfData.scale < tempRect.x) {
          tempRect.x = rect.x * pdfData.scale;
        }
        if (rect.y * pdfData.scale < tempRect.y) {
          tempRect.y = rect.y * pdfData.scale;
        }
        if (
          rect.x * pdfData.scale + rect.width * pdfData.scale >
          tempRect.x + tempRect.width
        ) {
          tempRect.width = rect.width * pdfData.scale;
        }
        if (
          rect.y * pdfData.scale + rect.height * pdfData.scale >
          tempRect.y + tempRect.height
        ) {
          tempRect.height = rect.height * pdfData.scale;
        }
      });

      return tempRect;
    };

    const rangeBounds = getBoundingRect(props.selectedAnnotation.data.rects);

    setTarget(rangeBounds);
  }, [props.selectedAnnotation]);

  return (
    <>
      {mobileView ? (
        <Modal
          isOpen={!!props.selectedAnnotation}
          toggle={() => {
            if (props.deselectAnnotation) {
              props.deselectAnnotation();
            }
          }}
          wrapClassName="rc-h-screen rc-overflow-auto rc-w-full"
        >
          <ModalHeader>
            <ModalTitle>Annotation Details</ModalTitle>
          </ModalHeader>
          <ModalBody>
            <ConnectedAnnotationItem annotation={props.selectedAnnotation} />
          </ModalBody>
        </Modal>
      ) : (
        <Popover open={!!props.selectedAnnotation}>
          <PopoverTrigger asChild>
            <div
              ref={targetRef}
              className="target"
              style={{
                position: 'absolute',
                top: target?.top,
                left: target?.left,
                width: target?.width,
                height: target?.height,
              }}
            ></div>
          </PopoverTrigger>
          <PopoverContent
            portalled={false}
            className="rc-bg-background rc-w-[25rem] rc-max-h-[var(--radix-popover-content-available-height)] rc-overflow-auto rc-z-[230] rc-rounded-lg rc-p-0"
            collisionPadding={8}
          >
            <ConnectedAnnotationItem annotation={props.selectedAnnotation} />
          </PopoverContent>
        </Popover>
      )}
    </>
  );
};
